import React, { useMemo, useState } from "react";
import ReportStepFrame from "components/report/ReportStepFrame";
import {
  TooltipGroup,
  useTooltipTrigger,
} from "components/report/useTooltipTrigger";
import RadarChart from "apps/user/learner/pages/woneindex/v3/components/radar-chart";
import styles from "./ScoreBreakdownStep.module.scss";

/** A radar polygon needs at least three vertices to be a shape at all. */
const MIN_RADAR_DOMAINS = 3;

/** Gridlines on the bar chart's value axis, as in the designs: 0…100 by 20. */
const BAR_AXIS_DIVISIONS = 5;

/** Below this, a distribution is a point or a line, not a shape worth drawing. */
const MIN_DISTRIBUTION_POINTS = 3;

/**
 * The lowest overall score the index can give. The curve's taper is kept
 * above it, so the chart never suggests anyone scored lower.
 */
const INDEX_FLOOR = 20;

/**
 * How near, as a share of the plot's width, the learner's marker can come to
 * the average's before its score and label move off the marker's centre.
 * Centred, the two lines of text are wide enough to cross the average's
 * line within about this distance.
 */
const LABEL_CLEARANCE_PERCENT = 6;

/** Where the badge goes inside the card's sentence. */
const BADGE_TOKEN = "{badge}";

/**
 * How far each control point reaches towards its neighbours. A sixth is the
 * standard Catmull-Rom spacing, which passes the curve exactly through every
 * point it is given.
 */
const SMOOTHING = 1 / 6;

interface Point {
  x: number;
  y: number;
}

/** One cubic piece of the curve, as drawn: rounded exactly as the path is. */
interface Segment {
  start: Point;
  c1: Point;
  c2: Point;
  end: Point;
}

/**
 * A curve through the measured points, rather than a line between them.
 *
 * A density curve drawn as straight segments reads as a set of readings; the
 * same points splined read as the distribution they sample, which is what
 * they are. The curve still passes exactly through every point, so it asserts
 * nothing extra about where people actually scored.
 *
 * Control points are clamped to the plot. Left unclamped, a spline overshoots
 * around a sharp peak and dips below the baseline, drawing negative density:
 * a shape we not only did not measure but which cannot exist.
 *
 * Their x is held within their own segment too. Where a short bucket gap
 * sits beside a long one, the neighbour's reach would otherwise carry a
 * control point past the segment's end, and the curve would double back on
 * itself: one score, two heights.
 *
 * Both the path and the markers read these segments, so a marker meets the
 * line that is drawn rather than an approximation of it.
 */
const splineSegments = (plotted: Point[], height: number): Segment[] => {
  const clamp = (y: number) => Math.min(height, Math.max(0, y));
  const at = (index: number) =>
    plotted[Math.min(plotted.length - 1, Math.max(0, index))];
  const round = (value: number) => Number(value.toFixed(2));
  const rounded = (point: Point) => ({ x: round(point.x), y: round(point.y) });

  return plotted.slice(0, -1).map((start, index) => {
    const before = at(index - 1);
    const end = at(index + 1);
    const after = at(index + 2);
    const within = (x: number) => Math.min(end.x, Math.max(start.x, x));
    return {
      start: rounded(start),
      c1: rounded({
        x: within(start.x + (end.x - before.x) * SMOOTHING),
        y: clamp(start.y + (end.y - before.y) * SMOOTHING),
      }),
      c2: rounded({
        x: within(end.x - (after.x - start.x) * SMOOTHING),
        y: clamp(end.y - (after.y - start.y) * SMOOTHING),
      }),
      end: rounded(end),
    };
  });
};

const bezier = (a: number, b: number, c: number, d: number, t: number) => {
  const u = 1 - t;
  return u * u * u * a + 3 * u * u * t * b + 3 * u * t * t * c + t * t * t * d;
};

/** Bisection steps to find a segment's parameter at an x: far below a pixel. */
const BISECTION_STEPS = 40;

/**
 * The drawn curve's height at an x, solved on the cubic that spans it. Off
 * the ends of the measured range the line runs along the baseline.
 *
 * Bisection is valid because every segment's x only increases with t: its
 * control points are held between its own ends.
 */
const segmentYAt = (
  segments: Segment[],
  x: number,
  baseline: number
): number => {
  const segment = segments.find(
    (piece) => piece.start.x <= x && x <= piece.end.x
  );
  if (!segment) return baseline;
  const xAt = (t: number) =>
    bezier(segment.start.x, segment.c1.x, segment.c2.x, segment.end.x, t);
  let low = 0;
  let high = 1;
  for (let step = 0; step < BISECTION_STEPS; step += 1) {
    const middle = (low + high) / 2;
    if (xAt(middle) < x) low = middle;
    else high = middle;
  }
  const t = (low + high) / 2;
  return bezier(segment.start.y, segment.c1.y, segment.c2.y, segment.end.y, t);
};

const segmentsPath = (segments: Segment[]): string =>
  segments
    .map(
      ({ c1, c2, end }) =>
        ` C ${c1.x},${c1.y} ${c2.x},${c2.y} ${end.x},${end.y}`
    )
    .join("");

/**
 * The measured buckets with an empty one added beyond each end, so the curve
 * meets the baseline there rather than stopping in mid-air.
 *
 * The bucket width is read off the data, as the narrowest gap between two
 * buckets, rather than agreed with the server. The added points stay within
 * the scores the index can give; where the data already reaches that limit
 * the curve drops straight to the baseline at it instead.
 */
const withTapers = (
  points: { score: number; density: number }[],
  outOf: number
): { score: number; density: number }[] => {
  const first = points[0].score;
  const last = points[points.length - 1].score;
  const bucketWidth = Math.min(
    ...points.slice(1).map((point, index) => point.score - points[index].score)
  );
  if (!(bucketWidth > 0)) return points;

  const below = Math.max(Math.min(INDEX_FLOOR, first), first - bucketWidth);
  const above = Math.min(Math.max(outOf, last), last + bucketWidth);
  return [
    ...(below < first ? [{ score: below, density: 0 }] : []),
    ...points,
    ...(above > last ? [{ score: above, density: 0 }] : []),
  ];
};

export interface ScoreBreakdownDomain {
  /** Stable key. Never rendered. */
  id: string;
  /** The domain's name, as the caller wants it read. */
  label: string;
  /**
   * The learner's score for this domain, or null when they have none — an
   * unanswered domain, or one whose data has not landed yet.
   */
  score?: number | null;
  /**
   * The domain's glyph on the bar chart's axis. Optional, and supplied by
   * the caller so no artwork is bundled into the step.
   */
  icon?: React.ReactNode;
}

export interface ScoreBreakdownDistributionPoint {
  /** The score this bucket sits at. */
  score: number;
  /**
   * How much of the population is at that score. Relative, not absolute —
   * counts, proportions and percentages all plot the same.
   */
  density: number;
}

export interface ScoreBreakdownPercentile {
  /** Where the learner sits on the distribution. */
  value: number;
  /** Where the population sits. */
  averageValue: number;
  /**
   * The measured spread of scores across the population, which the card
   * plots as-is. Omit it and no curve is drawn — the card shows the two
   * markers on their axis and nothing behind them. The step never derives
   * a shape of its own, so what a learner sees here is only ever something
   * we observed.
   */
  distribution?: ScoreBreakdownDistributionPoint[];
  /**
   * The card's sentence. A `{badge}` token is replaced by the badge, so the
   * catalogue keeps the whole sentence and a translator can put the figure
   * where their grammar needs it. Without the token the badge follows the
   * sentence.
   */
  heading: string;
  /** The figure called out inside the sentence, e.g. "2%". */
  badge?: string;
  /** Marks the learner's point on the curve. */
  youLabel: string;
  /** Marks the population's point on the curve. */
  averageLabel: string;
  /** The window the distribution covers, e.g. "Measured over 1 year". */
  footnote?: string;
}

export interface ScoreBreakdownStepProps {
  /** The step's title. */
  heading: string;
  /**
   * One entry per domain, in the order both charts draw them: clockwise
   * round the radar from the top, and left to right along the bars.
   */
  domains: ScoreBreakdownDomain[];
  /** What each score is out of. The designs show 100 throughout. */
  outOf?: number;
  /** Accessible name for the radar chart, which carries no visible title. */
  radarLabel: string;
  /** Accessible name for the bar chart, which carries no visible title. */
  barsLabel: string;
  /** Shown in place of a bar's value when the domain has no score. */
  missingScoreLabel: string;
  /** Shown instead of the charts when no domain has a score at all. */
  emptyMessage: string;
  /** The percentile card. Omit it and the card is simply absent. */
  percentile?: ScoreBreakdownPercentile;
  /** Told a domain's `id` each time the learner opens its bar's name. */
  onDomainOpen?: (id: string) => void;
}

interface ScoredDomain extends ScoreBreakdownDomain {
  score: number;
}

const hasScore = (domain: ScoreBreakdownDomain): domain is ScoredDomain =>
  typeof domain.score === "number" && Number.isFinite(domain.score);

/**
 * The spider chart: the same one the Index tab renders, fed the report's
 * domains. Only scored domains reach it — a polygon cannot have a hole.
 */
const DomainRadarCard: React.FC<{
  domains: ScoredDomain[];
  outOf: number;
  label: string;
}> = ({ domains, outOf, label }) => (
  <div className={styles.card} data-testid="score-breakdown-radar">
    <div
      className={styles["radar-holder"]}
      role="img"
      aria-label={label}
      data-testid="score-breakdown-radar-chart"
    >
      <RadarChart
        outline="polygon"
        chartData={domains.map((domain) => ({
          domain: domain.label,
          score: domain.score,
          fullMark: outOf,
        }))}
      />
    </div>
  </div>
);

/**
 * One bar, as the trigger for a tooltip naming its domain. The chart's axis
 * carries only glyphs, so the name is otherwise on the radar alone.
 *
 * The whole column answers, not just the bar, so a short bar or an unscored
 * stub is as easy to point at as a tall one. The tooltip sits over the bar's
 * top rather than under the column, where it would cover the glyph it names.
 */
const BarColumn: React.FC<{
  domain: ScoreBreakdownDomain;
  height?: string;
  align: "start" | "center" | "end";
  onOpen?: () => void;
}> = ({ domain, height, align, onOpen }) => {
  const { open, tooltipId, tooltipRef, rootProps, buttonProps } =
    useTooltipTrigger(onOpen);

  return (
    <li className={styles["bars-column"]}>
      <span
        {...rootProps}
        className={styles["bar-trigger"]}
        style={{ "--bar-height": height ?? "3px" } as React.CSSProperties}
      >
        {/* Named by its tooltip: the domain is all the tooltip says. */}
        <button
          {...buttonProps}
          aria-describedby={undefined}
          aria-labelledby={tooltipId}
          className={styles["bar-button"]}
        >
          <span
            className={styles.bar}
            data-scored={hasScore(domain)}
            data-testid={`score-breakdown-bar-${domain.id}`}
            style={height ? { height } : undefined}
          />
        </button>
        <span
          ref={tooltipRef}
          role="tooltip"
          id={tooltipId}
          className={styles["bar-tooltip"]}
          data-align={align}
          hidden={!open}
        >
          {domain.label}
        </span>
      </span>
    </li>
  );
};

/**
 * The bar chart: the same numbers as the radar, read as magnitudes rather
 * than as a shape. Unlike the radar it can show a domain with no score, so
 * every domain appears here, each in its own place in the order.
 */
const DomainBarsCard: React.FC<{
  domains: ScoreBreakdownDomain[];
  outOf: number;
  label: string;
  missingScoreLabel: string;
  onDomainOpen?: (id: string) => void;
}> = ({ domains, outOf, label, missingScoreLabel, onDomainOpen }) => {
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const tooltipGroup = useMemo(
    () => ({ active: activeTooltip, claim: setActiveTooltip }),
    [activeTooltip]
  );

  const ticks = Array.from(
    { length: BAR_AXIS_DIVISIONS + 1 },
    (_, index) => outOf - (index * outOf) / BAR_AXIS_DIVISIONS
  );

  const heightFor = (domain: ScoreBreakdownDomain) =>
    hasScore(domain)
      ? `${Number(
          Math.min(100, Math.max(0, (domain.score / outOf) * 100)).toFixed(2)
        )}%`
      : undefined;

  // The outermost bars sit against the card's edges, where a centred tooltip
  // would spill past it, so they hang theirs from their outer side.
  const alignAt = (index: number) =>
    index === 0 ? "start" : index === domains.length - 1 ? "end" : "center";

  return (
    <div className={styles.card} data-testid="score-breakdown-bars">
      {/*
        Three stacked rows, as the designs draw it: the bars, then the
        glyphs, then the values. The axis closes at the bars' foot, so the
        baseline sits directly under them rather than at the card's bottom
        edge with the legend floating inside the plot.

        A group rather than an image: the bars are buttons, and an image's
        children are presentational, so a screen reader could not reach them.
      */}
      <TooltipGroup.Provider value={tooltipGroup}>
        <div className={styles.bars} role="group" aria-label={label}>
          <ul className={styles["bars-axis"]} aria-hidden="true">
            {ticks.map((tick) => (
              <li
                key={tick}
                className={styles["bars-tick"]}
                style={{ top: `${100 - (tick / outOf) * 100}%` }}
              >
                {tick}
              </li>
            ))}
          </ul>

          <div className={styles["bars-plot"]}>
            <ul className={styles["bars-tracks"]}>
              {domains.map((domain, index) => (
                <BarColumn
                  key={domain.id}
                  domain={domain}
                  height={heightFor(domain)}
                  align={alignAt(index)}
                  onOpen={
                    onDomainOpen ? () => onDomainOpen(domain.id) : undefined
                  }
                />
              ))}
            </ul>

            <ul className={styles["bars-legend"]}>
              {domains.map((domain) => (
                <li key={domain.id} className={styles["bars-entry"]}>
                  {domain.icon && (
                    <span className={styles["bars-icon"]} aria-hidden="true">
                      {domain.icon}
                    </span>
                  )}
                  <span className={styles["bars-value"]}>
                    <span className={styles["bars-domain"]}>
                      {domain.label}
                    </span>
                    <span className={styles["bars-number"]}>
                      {hasScore(domain) ? domain.score : missingScoreLabel}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </TooltipGroup.Provider>
    </div>
  );
};

/**
 * The percentile card: where the learner's overall score falls against
 * everyone else's.
 *
 * The curve is only ever drawn from a distribution the caller measured and
 * passed in. It is never inferred from the learner's score and the average,
 * because a shape computed in here would be indistinguishable, to a learner,
 * from one we had measured — and we would be asserting both the shape and its
 * width without having observed either. With no distribution the card renders
 * the band alone: the two markers on their axis, and no shape behind them.
 */
const PercentileCard: React.FC<{
  percentile: ScoreBreakdownPercentile;
  outOf: number;
}> = ({ percentile, outOf }) => {
  const {
    value,
    averageValue,
    heading,
    badge,
    youLabel,
    averageLabel,
    distribution,
  } = percentile;

  // Gradient ids are document-global, so two cards on one page would
  // otherwise paint with each other's. React's ids carry colons, which a
  // url() reference does not reliably resolve.
  const gradientId = React.useId().replace(/:/g, "");
  const fillId = `percentile-fill-${gradientId}`;

  const width = 320;
  const height = 150;
  const clamp = (score: number) => Math.min(outOf, Math.max(0, score));
  const toX = (score: number) => (clamp(score) / outOf) * width;

  // The badge sits where the copy puts it. Without a token it follows the
  // sentence, and needs the space the token would have carried.
  //
  // Only the first token places the badge; splitting on every one of them and
  // taking two parts would silently swallow the text after a second token, so
  // a duplicated token in a catalogue would shorten the sentence with nothing
  // to show for it.
  const tokenAt = heading.indexOf(BADGE_TOKEN);
  const [headingBefore, headingAfter] =
    tokenAt === -1
      ? [badge ? `${heading} ` : heading, ""]
      : [
          heading.slice(0, tokenAt),
          heading.slice(tokenAt + BADGE_TOKEN.length),
        ];

  // A single point is a dot, not a distribution, so it gets no curve either.
  const points =
    distribution && distribution.length >= MIN_DISTRIBUTION_POINTS
      ? [...distribution].sort((a, b) => a.score - b.score)
      : null;

  // Densities are relative: the tallest observed bucket reaches the top of
  // the plot, so the caller can pass counts, proportions or anything
  // monotonic in frequency without agreeing a scale with us first.
  const peakDensity = points
    ? Math.max(...points.map((point) => Math.max(0, point.density)))
    : 0;

  // The curve's shape ends where the data does: each end tapers to the
  // baseline one bucket width beyond the last measured bucket, where the next
  // bucket out would have sat had anyone landed in it.
  const plotted =
    points && peakDensity > 0
      ? withTapers(points, outOf).map((point) => ({
          x: toX(point.score),
          y: height - (Math.max(0, point.density) / peakDensity) * height,
        }))
      : null;

  // The line then runs on along the baseline to both ends of the scale, as
  // the design draws it. These are straight segments, not part of the
  // spline: a spline through an extra point at 0 would slope up from it into
  // data that starts at the index floor, filling a wedge over empty scores.
  const segments = plotted ? splineSegments(plotted, height) : null;
  const path =
    plotted && segments
      ? (() => {
          const first = segments[0].start;
          const last = segments[segments.length - 1].end;
          const lead =
            first.x > 0
              ? `M 0,${height} L ${first.x},${height} L ${first.x},${first.y}`
              : `M ${first.x},${first.y}`;
          const trail =
            last.x < width ? ` L ${last.x},${height} L ${width},${height}` : "";
          return `${lead}${segmentsPath(segments)}${trail}`;
        })()
      : null;
  const closing = ` L ${width},${height} L 0,${height} Z`;

  // Where the drawn curve is at a score, so a marker meets it exactly.
  const curveYAt = (score: number): number =>
    segments ? segmentYAt(segments, toX(score), height) : height;

  const asPercent = (score: number) => (clamp(score) / outOf) * 100;
  // The plot is drawn in viewBox units and laid out in percentages, so a
  // height in one becomes a height in the other.
  const asHeightPercent = (y: number) => ((height - y) / height) * 100;

  const youAt = asPercent(value);
  const averageAt = asPercent(averageValue);
  // Close to the average, the learner's score and label hang on the side of
  // their point away from it, so they never sit across the average's line.
  const youLabelSide =
    Math.abs(youAt - averageAt) >= LABEL_CLEARANCE_PERCENT
      ? "center"
      : youAt >= averageAt
      ? "right"
      : "left";

  return (
    <section className={styles.card} data-testid="score-breakdown-percentile">
      <p
        className={styles["percentile-heading"]}
        data-testid="score-breakdown-percentile-heading"
      >
        {headingBefore}
        {badge && <span className={styles["percentile-badge"]}>{badge}</span>}
        {headingAfter}
      </p>

      <div className={styles["percentile-chart"]}>
        <div className={styles["percentile-plot"]}>
          {path && (
            <svg
              className={styles["percentile-curve"]}
              viewBox={`0 0 ${width} ${height}`}
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
              data-testid="score-breakdown-distribution"
            >
              {/*
                The fill fades towards the axis. The outline does not: it
                is the bars' own, set in the stylesheet.
              */}
              <defs>
                <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#ffffff" stopOpacity={0.35} />
                  <stop offset="1" stopColor="#ffffff" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <path
                d={`${path}${closing}`}
                fill={`url(#${fillId})`}
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          )}

          {/*
            The population's marker runs solid from the axis up to the
            curve, the learner's is dotted with a point where it meets the
            curve. The design distinguishes them that way round, and it is
            the learner's own position that earns the emphasis of a marked
            point.
          */}
          <span
            className={styles["percentile-marker"]}
            data-variant="average"
            data-testid="score-breakdown-percentile-average"
            style={{
              left: `${averageAt}%`,
              height: `${asHeightPercent(curveYAt(averageValue))}%`,
            }}
          />
          <span
            className={styles["percentile-marker"]}
            data-variant="you"
            data-label-side={youLabelSide}
            data-testid="score-breakdown-percentile-you"
            style={{
              left: `${youAt}%`,
              height: `${asHeightPercent(curveYAt(value))}%`,
            }}
          >
            <span className={styles["percentile-point"]} aria-hidden="true" />
            {/*
              The learner's score sits over their own point, above the
              label, rather than on the axis, where it collided with the
              average's whenever the two were close.
            */}
            <span className={styles["percentile-you-score"]}>{value}</span>
            <span className={styles["percentile-you-label"]}>{youLabel}</span>
          </span>
        </div>

        <div
          className={styles["percentile-axis"]}
          aria-hidden="true"
          data-testid="score-breakdown-percentile-axis"
        >
          <span className={styles["percentile-tick"]} style={{ left: "0%" }}>
            {clamp(0)}
          </span>
          <span
            className={styles["percentile-tick"]}
            style={{ left: `${averageAt}%` }}
          >
            {averageValue}
          </span>
          <span className={styles["percentile-tick"]} style={{ left: "100%" }}>
            {outOf}
          </span>
        </div>

        <div className={styles["percentile-average-track"]}>
          <p
            className={styles["percentile-average-label"]}
            style={{ left: `${averageAt}%` }}
          >
            {averageLabel}
          </p>
        </div>
      </div>

      {percentile.footnote && (
        <p
          className={styles["percentile-footnote"]}
          data-testid="score-breakdown-percentile-footnote"
        >
          {percentile.footnote}
        </p>
      )}
    </section>
  );
};

/**
 * "What makes up your score" — the overall score decomposed by domain.
 *
 * The three cards are independent siblings that share nothing but the
 * `domains` prop, so any one of them can be deleted without touching the
 * others. That matters here: the radar and the bars deliberately say the
 * same thing two ways, and the review has not yet settled which survives.
 */
const ScoreBreakdownStep: React.FC<ScoreBreakdownStepProps> = ({
  heading,
  domains,
  outOf = 100,
  radarLabel,
  barsLabel,
  missingScoreLabel,
  emptyMessage,
  percentile,
  onDomainOpen,
}) => {
  const scored = domains.filter(hasScore);

  return (
    <ReportStepFrame>
      <div className={styles.step} data-testid="score-breakdown-step">
        <h2 className={styles.heading}>{heading}</h2>

        {scored.length === 0 ? (
          <p className={styles.empty} data-testid="score-breakdown-empty">
            {emptyMessage}
          </p>
        ) : (
          <div className={styles.cards}>
            {scored.length >= MIN_RADAR_DOMAINS && (
              <DomainRadarCard
                domains={scored}
                outOf={outOf}
                label={radarLabel}
              />
            )}
            <DomainBarsCard
              domains={domains}
              outOf={outOf}
              label={barsLabel}
              missingScoreLabel={missingScoreLabel}
              onDomainOpen={onDomainOpen}
            />
            {percentile && (
              <PercentileCard percentile={percentile} outOf={outOf} />
            )}
          </div>
        )}
      </div>
    </ReportStepFrame>
  );
};

export default ScoreBreakdownStep;
